import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useSearchParams } from 'react-router-dom';
import { useCart } from '../context/CartContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';

/* ── Animaciones ── */
const fadeUp = keyframes`
  from { opacity:0; transform:translateY(24px); }
  to   { opacity:1; transform:translateY(0); }
`;
const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
`;
const circleAnim = keyframes`
  from { stroke-dashoffset: 201; }
  to   { stroke-dashoffset: 0; }
`;
const checkAnim = keyframes`
  from { stroke-dashoffset: 50; }
  to   { stroke-dashoffset: 0; }
`;
const popIn = keyframes`
  0%   { transform: scale(0.6); opacity: 0; }
  70%  { transform: scale(1.08); }
  100% { transform: scale(1);   opacity: 1; }
`;

/* ── Layout ── */
const Shell = styled.div`
  min-height: 100vh;
  background: #0D0B08;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
  gap: 0;
`;

const Wrapper = styled.div`
  width: 100%;
  max-width: 520px;
  animation: ${fadeUp} 0.6s cubic-bezier(0.16,1,0.3,1) both;
`;

/* ── Checkmark SVG animado ── */
const CheckWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 28px;
  animation: ${popIn} 0.55s cubic-bezier(0.16,1,0.3,1) both 0.05s;
  filter: drop-shadow(0 0 18px rgba(52,211,153,0.35));
`;

const CirclePath = styled.circle`
  stroke-dasharray: 201;
  stroke-dashoffset: 201;
  animation: ${circleAnim} 0.6s cubic-bezier(0.4,0,0.2,1) forwards 0.1s;
`;

const CheckPath = styled.polyline`
  stroke-dasharray: 50;
  stroke-dashoffset: 50;
  animation: ${checkAnim} 0.35s cubic-bezier(0.4,0,0.2,1) forwards 0.65s;
`;

/* ── Título ── */
const Title = styled.h1`
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(2rem, 5vw, 2.8rem);
  font-weight: 600;
  color: #FFFDF7;
  text-align: center;
  margin: 0 0 10px;
  background: linear-gradient(90deg, #FFFDF7 0%, #C07020 50%, #FFFDF7 100%);
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${shimmer} 3s linear infinite;
`;

const Sub = styled.p`
  text-align: center;
  font-size: 0.92rem;
  color: rgba(255,253,247,0.5);
  line-height: 1.7;
  margin: 0 0 36px;
`;

/* ── Secciones ── */
const Section = styled.div`
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: 12px;
`;

const SectionHead = styled.div`
  padding: 10px 18px;
  background: rgba(255,255,255,0.03);
  border-bottom: 1px solid rgba(255,255,255,0.05);
  font-size: 0.65rem;
  letter-spacing: 0.15em;
  color: rgba(255,253,247,0.3);
  text-transform: uppercase;
`;

const SectionBody = styled.div`
  padding: 14px 18px;
`;

/* ── Fila de producto ── */
const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 7px 0;
  font-size: 0.88rem;
  color: rgba(255,253,247,0.8);
  border-bottom: 1px solid rgba(255,255,255,0.04);
  &:last-child { border-bottom: none; }
`;

const ItemQty = styled.span`
  font-size: 0.72rem;
  color: #C07020;
  background: rgba(192,112,32,0.12);
  border-radius: 4px;
  padding: 2px 6px;
  margin-right: 10px;
  font-weight: 600;
`;

const ItemPrice = styled.span`
  color: rgba(255,253,247,0.45);
  font-size: 0.82rem;
`;

/* ── Total ── */
const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 18px;
  border-top: 1px solid rgba(255,255,255,0.07);
  font-size: 0.88rem;
  color: rgba(255,253,247,0.5);
  font-weight: 500;
  span:last-child {
    font-size: 1.1rem;
    font-weight: 700;
    color: #C07020;
  }
`;

/* ── Info de envío ── */
const InfoLine = styled.div`
  font-size: 0.85rem;
  color: rgba(255,253,247,0.65);
  line-height: 1.8;
  display: flex;
  gap: 10px;
  align-items: flex-start;
  span:first-child { flex-shrink: 0; opacity: 0.6; }
`;

/* ── Botones ── */
const BtnRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 24px;
  flex-wrap: wrap;
`;

const HomeBtn = styled(Link)`
  flex: 1;
  min-width: 140px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 14px 24px;
  border-radius: 999px;
  background: linear-gradient(135deg, #C07020, #7C3A00);
  color: #FFFDF7;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-decoration: none;
  transition: opacity 0.2s, transform 0.2s;
  &:hover { opacity: 0.88; transform: translateY(-1px); }
`;

const OutlineBtn = styled.button`
  flex: 1;
  min-width: 140px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 14px 24px;
  border-radius: 999px;
  border: 1px solid rgba(192,112,32,0.4);
  background: transparent;
  color: #C07020;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
  &:hover { background: rgba(192,112,32,0.08); border-color: #C07020; }
`;

/* ── Order code box ── */
const OrderCodeBox = styled.div`
  background: rgba(192,112,32,0.07);
  border: 1px solid rgba(192,112,32,0.25);
  border-radius: 14px;
  padding: 20px 22px;
  margin-bottom: 12px;
  text-align: center;
`;

const CodeLabel = styled.div`
  font-size: 0.62rem;
  letter-spacing: 0.18em;
  color: rgba(255,253,247,0.35);
  text-transform: uppercase;
  margin-bottom: 8px;
`;

const CodeValue = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 1.6rem;
  font-weight: 700;
  color: #C07020;
  letter-spacing: 0.12em;
  margin-bottom: 12px;
`;

const CodeHint = styled.div`
  font-size: 0.76rem;
  color: rgba(255,253,247,0.38);
  line-height: 1.6;
  margin-bottom: 14px;
`;

const TrackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 22px;
  border-radius: 999px;
  background: rgba(192,112,32,0.15);
  border: 1px solid rgba(192,112,32,0.35);
  color: #C07020;
  font-size: 0.8rem;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.2s;
  &:hover { background: rgba(192,112,32,0.25); }
`;

const SessionId = styled.div`
  display: none;
`;

/* ── Loading skeleton ── */
const Pulse = keyframes`
  0%,100% { opacity: 0.3; } 50% { opacity: 0.6; }
`;
const SkeletonLine = styled.div`
  height: 14px;
  border-radius: 6px;
  background: rgba(255,255,255,0.08);
  animation: ${Pulse} 1.4s ease-in-out infinite;
  margin-bottom: 8px;
  width: ${({ w }) => w || '100%'};
`;

export default function SuccessPage() {
  const [params]    = useSearchParams();
  const [order,     setOrder]   = useState(null);
  const [loading,   setLoading] = useState(true);
  const sessionId   = params.get('session_id');
  const { clearCart } = useCart();

  useEffect(() => { clearCart(); }, []); // eslint-disable-line

  useEffect(() => {
    if (!sessionId) { setLoading(false); return; }
    fetch(`${API}/api/checkout/session/${sessionId}`)
      .then(r => r.json())
      .then(d => { setOrder(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [sessionId]);

  const isMxn = order?.currency === 'mxn';
  const sym   = isMxn ? 'MX$' : '$';
  const fmt   = (cents) => `${sym}${(cents / 100).toFixed(2)}`;

  const addr = order?.shippingAddress;
  const addrStr = addr
    ? [addr.line1, addr.line2, addr.city, addr.state, addr.postal_code, addr.country].filter(Boolean).join(', ')
    : null;

  const code = order?.orderCode || sessionId?.slice(-12).toUpperCase() || '—';

  function downloadReceipt() {
    const date = new Date().toLocaleDateString('es-MX', { year:'numeric', month:'long', day:'numeric' });
    const rows = (order?.lineItems || []).map(item =>
      `<tr><td style="padding:8px 0;border-bottom:1px solid #eee;">${item.quantity}× ${item.description}</td>
       <td style="padding:8px 0;border-bottom:1px solid #eee;text-align:right;">${fmt(item.amount_total)}</td></tr>`
    ).join('');

    const html = `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8">
<title>Recibo — ${code}</title>
<style>
  body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;max-width:520px;margin:40px auto;color:#1A1208;padding:0 20px;}
  h1{font-size:22px;margin:0 0 4px;}
  .brand{font-size:13px;color:#7C3A00;letter-spacing:.15em;text-transform:uppercase;margin-bottom:24px;}
  .code-box{background:#f7f4f0;border-radius:10px;padding:16px 20px;margin-bottom:24px;text-align:center;}
  .code-label{font-size:11px;color:#9B8A78;letter-spacing:.15em;text-transform:uppercase;margin-bottom:6px;}
  .code-value{font-family:'Courier New',monospace;font-size:24px;font-weight:700;color:#C07020;letter-spacing:.1em;}
  table{width:100%;border-collapse:collapse;margin-bottom:16px;}
  th{text-align:left;font-size:11px;letter-spacing:.1em;color:#9B8A78;padding-bottom:8px;border-bottom:2px solid #1A1208;}
  .total{display:flex;justify-content:space-between;font-weight:700;font-size:16px;padding-top:12px;border-top:2px solid #1A1208;}
  .meta{font-size:13px;color:#6B5C4A;margin-top:24px;line-height:1.8;}
  .hint{margin-top:24px;font-size:12px;color:#9B8A78;text-align:center;border-top:1px solid #eee;padding-top:16px;}
</style></head><body>
<div class="brand">Hacienda Real · Cajeta Artesanal</div>
<h1>Recibo de compra</h1>
<p style="font-size:13px;color:#9B8A78;margin:4px 0 24px;">${date}</p>
<div class="code-box">
  <div class="code-label">Código de pedido</div>
  <div class="code-value">${code}</div>
</div>
<table><thead><tr><th>Producto</th><th style="text-align:right;">Importe</th></tr></thead>
<tbody>${rows}</tbody></table>
<div class="total"><span>Total pagado</span><span>${fmt(order?.amountTotal || 0)}</span></div>
${order?.customerEmail ? `<div class="meta">📧 ${order.customerEmail}</div>` : ''}
${addrStr ? `<div class="meta">📦 ${addrStr}</div>` : ''}
<div class="hint">Guarda tu código <strong>${code}</strong> para rastrear tu pedido en haciendareal.com</div>
</body></html>`;

    const blob = new Blob([html], { type: 'text/html' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `recibo-${code}.html`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Shell>
      <Wrapper>
        <CheckWrap>
          <svg width="88" height="88" viewBox="0 0 88 88" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* fondo sutil */}
            <circle cx="44" cy="44" r="42" fill="rgba(52,211,153,0.08)" />
            {/* círculo animado */}
            <CirclePath
              cx="44" cy="44" r="32"
              stroke="#34D399" strokeWidth="3.5"
              strokeLinecap="round" fill="none"
              transform="rotate(-90 44 44)"
            />
            {/* check animado */}
            <CheckPath
              points="28,44 39,55 60,33"
              stroke="#34D399" strokeWidth="4"
              strokeLinecap="round" strokeLinejoin="round"
              fill="none"
            />
          </svg>
        </CheckWrap>

        <Title>¡Pedido confirmado!</Title>
        <Sub>
          Gracias por tu compra. En breve recibirás<br />un correo con todos los detalles.
        </Sub>

        {/* Order code */}
        {!loading && code !== '—' && (
          <OrderCodeBox>
            <CodeLabel>Código de pedido</CodeLabel>
            <CodeValue>{code}</CodeValue>
            <CodeHint>
              Guarda este código — con él y tu correo electrónico<br />
              puedes consultar el estado de tu pedido en cualquier momento.
            </CodeHint>
            <TrackLink to={`/track?code=${encodeURIComponent(code)}&email=${encodeURIComponent(order?.customerEmail || '')}`}>
              📦 Rastrear mi pedido →
            </TrackLink>
          </OrderCodeBox>
        )}

        {/* Productos */}
        <Section>
          <SectionHead>Productos</SectionHead>
          <SectionBody>
            {loading && [80, 65, 90].map((w, i) => <SkeletonLine key={i} w={`${w}%`} />)}
            {!loading && order?.lineItems?.map((item, i) => (
              <ItemRow key={i}>
                <div><ItemQty>{item.quantity}×</ItemQty>{item.description}</div>
                <ItemPrice>{fmt(item.amount_total)}</ItemPrice>
              </ItemRow>
            ))}
            {!loading && !order?.lineItems?.length && (
              <div style={{ color: 'rgba(255,253,247,0.3)', fontSize: '.82rem' }}>Sin detalle disponible</div>
            )}
          </SectionBody>
          {order && (
            <TotalRow>
              <span>Total pagado</span>
              <span>{fmt(order.amountTotal)}</span>
            </TotalRow>
          )}
        </Section>

        {/* Envío */}
        {(loading || addrStr || order?.customerEmail) && (
          <Section>
            <SectionHead>Datos de entrega</SectionHead>
            <SectionBody style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {loading && [70, 55].map((w, i) => <SkeletonLine key={i} w={`${w}%`} />)}
              {!loading && order?.customerEmail && <InfoLine><span>📧</span><span>{order.customerEmail}</span></InfoLine>}
              {!loading && order?.customerPhone  && <InfoLine><span>📱</span><span>{order.customerPhone}</span></InfoLine>}
              {!loading && addrStr               && <InfoLine><span>📦</span><span>{addrStr}</span></InfoLine>}
            </SectionBody>
          </Section>
        )}

        <BtnRow>
          <HomeBtn to="/">← Volver a la tienda</HomeBtn>
          {order && (
            <OutlineBtn onClick={downloadReceipt}>
              ⬇ Descargar recibo
            </OutlineBtn>
          )}
        </BtnRow>

        <SessionId />
      </Wrapper>
    </Shell>
  );
}

